import React, { useState, useRef, useEffect, useMemo } from 'react';
import { Range, getTrackBackground } from 'react-range';
import { useFavoriteStores } from '../../contexts/UserPreferencesContext';
import { usePlacesAutocomplete } from '../../hooks/usePlacesAutocomplete';
import { useAutocompleteKeyboard } from '../../hooks/useAutocompleteKeyboard';
import { dedupeByStoreName } from '../../utils/deduplication';
import { FAVORITE_MODE_AUTO, FAVORITE_MODE_BRAND, FAVORITE_MODE_OPTIONS, FAVORITE_MODE_COPY } from '../../utils/favorites';
import FavoriteModeHelp from '../Favorites/FavoriteModeHelp';
import './FiltersModal.css';

export default function FiltersModal({
  isOpen,
  onClose,
  // Homes
  showRentals,
  onShowRentalsChange,
  showFFMarkers,
  onShowFFMarkersChange,
  showTenantListingsToggle = false,
  showTenantCustomListings = false,
  onShowTenantCustomListingsChange = () => {},
  petsFilter,
  onPetsFilterChange,
  ffMinPrice,
  onFfMinPriceChange,
  ffMaxPrice,
  onFfMaxPriceChange,
  ffPriceMinAvailable,
  ffPriceMaxAvailable,
  ffListingPrices = [],
  ffMinBeds,
  onFfMinBedsChange,
  ffMinBaths,
  onFfMinBathsChange,
  // Places
  showStoreMarkers,
  onShowStoreMarkersChange,
  showGymMarkers,
  onShowGymMarkersChange,
  showThingsToDo = false,
  onShowThingsToDoChange = () => {},
  // Map/display filters
  crimeLayerOpacity,
  onCrimeLayerOpacityChange,
  showFurnishedFinderControls = true,
  showCrimeControls = true,
}) {
  const { favorites, addFavorite, updateFavorite, removeFavorite } = useFavoriteStores();
  const [newStoreName, setNewStoreName] = useState('');
  const [favoriteMode, setFavoriteMode] = useState(FAVORITE_MODE_AUTO);
  const [showStoreDropdown, setShowStoreDropdown] = useState(false);
  const [highlightedStoreIndex, setHighlightedStoreIndex] = useState(-1);
  const [minPriceInput, setMinPriceInput] = useState('');
  const [maxPriceInput, setMaxPriceInput] = useState('');
  const storeInputWrapperRef = useRef(null);
  const storeListRef = useRef(null);

  const storeAc = usePlacesAutocomplete(newStoreName);
  const favoriteModeCopy = FAVORITE_MODE_COPY[favoriteMode] || FAVORITE_MODE_COPY[FAVORITE_MODE_BRAND];
  const storeSuggestionsDeduped = useMemo(
    () => dedupeByStoreName(storeAc.suggestions),
    [storeAc.suggestions]
  );
  const showStoreSuggestions =
    showStoreDropdown && (storeSuggestionsDeduped.length > 0 || storeAc.loading);

  const priceBounds = useMemo(() => {
    const hasBounds = ffPriceMinAvailable != null && ffPriceMaxAvailable != null;
    const priceMin = hasBounds ? ffPriceMinAvailable : 0;
    const priceMax =
      hasBounds && ffPriceMaxAvailable > priceMin
        ? ffPriceMaxAvailable
        : (hasBounds ? priceMin + 100 : 5000);
    return { priceMin, priceMax };
  }, [ffPriceMinAvailable, ffPriceMaxAvailable]);

  const priceHistogramBuckets = useMemo(() => {
    const { priceMin, priceMax } = priceBounds;
    const numBuckets = 24;
    if (priceMax <= priceMin || !ffListingPrices.length) {
      return { counts: Array(numBuckets).fill(0), maxCount: 1 };
    }
    const counts = Array(numBuckets).fill(0);
    const span = priceMax - priceMin;
    for (const p of ffListingPrices) {
      if (p < priceMin || p > priceMax) continue;
      const t = span > 0 ? (p - priceMin) / span : 0;
      const i = Math.min(numBuckets - 1, Math.floor(t * numBuckets));
      counts[i] += 1;
    }
    const maxCount = Math.max(1, ...counts);
    return { counts, maxCount };
  }, [ffListingPrices, priceBounds]);

  useEffect(() => {
    setHighlightedStoreIndex(-1);
  }, [storeSuggestionsDeduped]);

  useEffect(() => {
    function handleClickOutside(e) {
      if (storeInputWrapperRef.current?.contains(e.target)) return;
      setShowStoreDropdown(false);
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (highlightedStoreIndex >= 0 && storeListRef.current) {
      const item = storeListRef.current.children[highlightedStoreIndex];
      item?.scrollIntoView({ block: 'nearest' });
    }
  }, [highlightedStoreIndex]);

  useEffect(() => {
    function handleEscape(e) {
      if (e.defaultPrevented) return;
      if (e.key === 'Escape') onClose();
    }
    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = '';
    };
  }, [isOpen, onClose]);

  // Sync typed price inputs when slider or bounds change
  useEffect(() => {
    if (!isOpen) return;
    const { priceMin, priceMax } = priceBounds;
    const minVal = Math.max(priceMin, Math.min(ffMinPrice ?? priceMin, priceMax));
    const maxVal = Math.max(priceMin, Math.min(ffMaxPrice ?? priceMax, priceMax));
    setMinPriceInput(String(minVal));
    setMaxPriceInput(String(maxVal));
  }, [isOpen, ffMinPrice, ffMaxPrice, priceBounds]);

  function handlePetsChange(value) {
    onPetsFilterChange(value);
  }

  function handlePriceRangeChange(values) {
    if (Array.isArray(values) && values.length >= 2) {
      onFfMinPriceChange(Math.round(values[0]));
      onFfMaxPriceChange(Math.round(values[1]));
    }
  }

  function handleMinPriceInputChange(e) {
    const raw = e.target.value.replace(/[^0-9]/g, '');
    setMinPriceInput(raw);
  }

  function handleMaxPriceInputChange(e) {
    const raw = e.target.value.replace(/[^0-9]/g, '');
    setMaxPriceInput(raw);
  }

  function commitMinPriceInput(priceMin, priceMax) {
    const num = parseInt(minPriceInput, 10);
    const clamped = Number.isFinite(num)
      ? Math.max(priceMin, Math.min(num, priceMax))
      : priceMin;
    setMinPriceInput(String(clamped));
    onFfMinPriceChange(clamped);
  }

  function commitMaxPriceInput(priceMin, priceMax) {
    const num = parseInt(maxPriceInput, 10);
    const clamped = Number.isFinite(num)
      ? Math.max(priceMin, Math.min(num, priceMax))
      : priceMax;
    setMaxPriceInput(String(clamped));
    onFfMaxPriceChange(clamped);
  }

  function handleMinBedsChange(value) {
    const num = Number(value);
    if (Number.isNaN(num)) return;
    onFfMinBedsChange(Math.max(0, Math.min(num, 10)));
  }

  function handleMinBathsChange(value) {
    const num = Number(value);
    if (Number.isNaN(num)) return;
    onFfMinBathsChange(Math.max(0, Math.min(num, 10)));
  }

  function commitStoreName(name, suggestion = null) {
    const trimmed = (name || '').trim();
    if (!trimmed) return;
    addFavorite({
      label: trimmed,
      mode: favoriteMode,
      placeId: suggestion?.place_id || '',
      query: trimmed,
      description: suggestion?.structured_formatting?.secondary_text || '',
    });
    setNewStoreName('');
    setShowStoreDropdown(false);
    setHighlightedStoreIndex(-1);
  }

  function handleAddStoreFromSuggestion(suggestion) {
    commitStoreName(suggestion.structured_formatting?.main_text || suggestion.description, suggestion);
    storeAc.resetSession();
  }

  function handleAddManual(e) {
    e.preventDefault();
    commitStoreName(newStoreName);
  }

  const handleStoreKeyDown = useAutocompleteKeyboard({
    suggestions: storeSuggestionsDeduped,
    isOpen: showStoreSuggestions,
    onSelect: (s) => handleAddStoreFromSuggestion(s),
    onClose: () => { setShowStoreDropdown(false); setHighlightedStoreIndex(-1); },
    highlightedIndex: highlightedStoreIndex,
    setHighlightedIndex: setHighlightedStoreIndex,
    onEnterNoSelection: () => commitStoreName(newStoreName),
  });

  if (!isOpen) return null;

  return (
    <div
      className="filters-modal-overlay"
      role="dialog"
      aria-modal="true"
      aria-labelledby="filters-modal-title"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="filters-modal">
        <div className="filters-modal__header">
          <h2 id="filters-modal-title" className="filters-modal__title">
            Filters
          </h2>
          <button
            type="button"
            className="filters-modal__close"
            onClick={onClose}
            aria-label="Close filters"
          >
            ×
          </button>
        </div>

        <div className="filters-modal__body">
          {/* Section: Homes */}
          {showFurnishedFinderControls && (
          <section className="filters-modal__section" aria-labelledby="filters-homes-heading">
            <h3 id="filters-homes-heading" className="filters-modal__section-title">
              Homes
            </h3>
            <div className="filters-modal__row">
              <label className="filters-modal__checkbox-label">
                <input
                  type="checkbox"
                  checked={showRentals}
                  onChange={(e) => onShowRentalsChange(e.target.checked)}
                  aria-label="Show rentals on map"
                />
                <span>Rentals</span>
              </label>
            </div>
            {showRentals && (
            <div className="filters-modal__subsection">
              <div className="filters-modal__row">
                <label className="filters-modal__checkbox-label">
                  <input
                    type="checkbox"
                    checked={showFFMarkers}
                    onChange={(e) => onShowFFMarkersChange(e.target.checked)}
                    aria-label="Show scraped rental listings on map"
                  />
                  <span className="filters-modal__checkbox-swatch filters-modal__checkbox-swatch--scraped" aria-hidden="true" />
                  <span>Scraped rentals</span>
                </label>
              </div>
              {showTenantListingsToggle && (
                <div className="filters-modal__row">
                  <label className="filters-modal__checkbox-label">
                    <input
                      type="checkbox"
                      checked={showTenantCustomListings}
                      onChange={(e) => onShowTenantCustomListingsChange(e.target.checked)}
                      aria-label="Show housing request listings on map"
                    />
                    <span className="filters-modal__checkbox-swatch filters-modal__checkbox-swatch--housing" aria-hidden="true" />
                    <span>Housing requests</span>
                  </label>
                </div>
              )}
            </div>
            )}
            {showFFMarkers && (
            <div className="filters-modal__subsection">
              <span className="filters-modal__subsection-title">Filters</span>
              <div className="filters-modal__field">
                <label htmlFor="filters-pets" className="filters-modal__label">
                  Pets
                </label>
                <select
                  id="filters-pets"
                  className="filters-modal__select"
                  value={petsFilter}
                  onChange={(e) => handlePetsChange(e.target.value)}
                  aria-label="Filter properties by pets allowed"
                >
                  <option value="all">Show both</option>
                  <option value="pets">Pets allowed</option>
                  <option value="no-pets">No pets</option>
                </select>
              </div>
              <div className="filters-modal__field">
                <label id="filters-price-label" className="filters-modal__label">
                  Price (per month)
                </label>
                {(() => {
                  const { priceMin, priceMax } = priceBounds;
                  const minVal = Math.max(priceMin, Math.min(ffMinPrice ?? priceMin, priceMax));
                  const maxVal = Math.max(priceMin, Math.min(ffMaxPrice ?? priceMax, priceMax));
                  const { counts: bucketCounts, maxCount } = priceHistogramBuckets;
                  const step = 50;
                  const snap = (v) => Math.max(priceMin, Math.min(priceMax, Math.round((v - priceMin) / step) * step + priceMin));
                  const sliderValues = [snap(minVal), snap(maxVal)];
                  return (
                    <div className="filters-modal__price-range-wrap">
                      {ffListingPrices.length > 0 && (
                        <div
                          className="filters-modal__price-histogram"
                          role="img"
                          aria-label="Number of listings by price"
                        >
                          {bucketCounts.map((count, i) => (
                            <div
                              key={i}
                              className="filters-modal__price-histogram-bar"
                              style={{
                                height: `${(count / maxCount) * 100}%`,
                              }}
                              title={`${count} listing${count !== 1 ? 's' : ''}`}
                            />
                          ))}
                        </div>
                      )}
                      <div className="filters-modal__price-range">
                        <div className="filters-modal__price-input-wrap">
                          <span className="filters-modal__price-input-prefix">$</span>
                          <input
                            type="text"
                            inputMode="numeric"
                            className="filters-modal__price-input"
                            value={minPriceInput}
                            onChange={handleMinPriceInputChange}
                            onBlur={() => commitMinPriceInput(priceMin, priceMax)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') {
                                e.target.blur();
                              }
                            }}
                            aria-label="Minimum monthly price (type a number)"
                          />
                        </div>
                        <div className="filters-modal__price-range-slider-wrap">
                          <Range
                            label="Monthly price range"
                            labelledBy="filters-price-label"
                            step={step}
                            min={priceMin}
                            max={priceMax}
                            values={sliderValues}
                            onChange={handlePriceRangeChange}
                            renderTrack={({ props: trackProps, children }) => (
                              <div
                                {...trackProps}
                                className="filters-modal__price-range-track"
                                style={{
                                  ...trackProps.style,
                                  background: getTrackBackground({
                                    values: sliderValues,
                                    min: priceMin,
                                    max: priceMax,
                                    colors: [
                                      'var(--color-border)',
                                      'var(--color-accent)',
                                      'var(--color-border)',
                                    ],
                                  }),
                                }}
                              >
                                {children}
                              </div>
                            )}
                            renderThumb={({ props: thumbProps, index }) => (
                              <div
                                {...thumbProps}
                                key={thumbProps.key}
                                className="filters-modal__price-range-thumb"
                                aria-label={index === 0 ? 'Minimum monthly price' : 'Maximum monthly price'}
                              />
                            )}
                          />
                        </div>
                        <div className="filters-modal__price-input-wrap">
                          <span className="filters-modal__price-input-prefix">$</span>
                          <input
                            type="text"
                            inputMode="numeric"
                            className="filters-modal__price-input"
                            value={maxPriceInput}
                            onChange={handleMaxPriceInputChange}
                            onBlur={() => commitMaxPriceInput(priceMin, priceMax)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') {
                                e.target.blur();
                              }
                            }}
                            aria-label="Maximum monthly price (type a number)"
                          />
                        </div>
                      </div>
                    </div>
                  );
                })()}
              </div>
              <div className="filters-modal__field">
                <label htmlFor="filters-beds" className="filters-modal__label">
                  Minimum beds
                </label>
                <select
                  id="filters-beds"
                  className="filters-modal__select"
                  value={String(ffMinBeds ?? 0)}
                  onChange={(e) => handleMinBedsChange(e.target.value)}
                  aria-label="Minimum number of bedrooms"
                >
                  <option value="0">Any</option>
                  <option value="1">1+</option>
                  <option value="2">2+</option>
                  <option value="3">3+</option>
                  <option value="4">4+</option>
                  <option value="5">5+</option>
                </select>
              </div>
              <div className="filters-modal__field">
                <label htmlFor="filters-baths" className="filters-modal__label">
                  Minimum baths
                </label>
                <select
                  id="filters-baths"
                  className="filters-modal__select"
                  value={String(ffMinBaths ?? 0)}
                  onChange={(e) => handleMinBathsChange(e.target.value)}
                  aria-label="Minimum number of bathrooms"
                >
                  <option value="0">Any</option>
                  <option value="1">1+</option>
                  <option value="1.5">1.5+</option>
                  <option value="2">2+</option>
                  <option value="2.5">2.5+</option>
                  <option value="3">3+</option>
                </select>
              </div>
            </div>
            )}
          </section>
          )}

          {/* Section: Places */}
          <section className="filters-modal__section" aria-labelledby="filters-places-heading">
            <h3 id="filters-places-heading" className="filters-modal__section-title">
              Places
            </h3>
            <div className="filters-modal__subsection">
              <span className="filters-modal__subsection-title">Favorites</span>
              <p className="filters-modal__hint">
                Add favorites and choose whether they should behave like a brand / chain or something broad when you search.
              </p>
              <div className="filters-modal__hint filters-modal__hint--with-help">
                <span>{favoriteModeCopy.examples}</span>
                <FavoriteModeHelp />
              </div>
              <form className="filters-modal__add-form" onSubmit={handleAddManual}>
                <div
                  className="filters-modal__add-row"
                  ref={storeInputWrapperRef}
                >
                  <div className="filters-modal__input-wrap">
                    <input
                      type="text"
                      role="combobox"
                      className={`filters-modal__input ${storeAc.loading ? 'filters-modal__input--loading' : ''}`}
                      value={newStoreName}
                      onChange={(e) => {
                        setNewStoreName(e.target.value);
                        setShowStoreDropdown(true);
                      }}
                      onFocus={() => setShowStoreDropdown(true)}
                      onKeyDown={handleStoreKeyDown}
                      placeholder={favoriteModeCopy.placeholder}
                      autoComplete="off"
                      aria-autocomplete="list"
                      aria-expanded={showStoreSuggestions}
                      aria-controls="filters-store-suggestions"
                    />
                    {storeAc.loading && (
                      <span className="filters-modal__spinner" aria-hidden="true" />
                    )}
                    {showStoreSuggestions && (
                      <ul
                        id="filters-store-suggestions"
                        className="filters-modal__suggestions"
                        ref={storeListRef}
                        role="listbox"
                      >
                        {storeAc.loading && storeSuggestionsDeduped.length === 0 ? (
                          <li className="filters-modal__suggestion filters-modal__suggestion--loading">
                            Searching…
                          </li>
                        ) : (
                          storeSuggestionsDeduped.map((suggestion, i) => (
                            <li
                              key={`${(suggestion.structured_formatting?.main_text || suggestion.description || '').trim().toLowerCase()}-${i}`}
                              className={`filters-modal__suggestion ${
                                i === highlightedStoreIndex ? 'filters-modal__suggestion--highlighted' : ''
                              }`}
                              role="option"
                              aria-selected={i === highlightedStoreIndex}
                              onMouseEnter={() => setHighlightedStoreIndex(i)}
                              onMouseDown={(e) => {
                                e.preventDefault();
                                handleAddStoreFromSuggestion(suggestion);
                              }}
                            >
                              <span className="filters-modal__suggestion-main">
                                {suggestion.structured_formatting?.main_text || suggestion.description}
                              </span>
                            </li>
                          ))
                        )}
                      </ul>
                    )}
                  </div>
                  <select
                    className="filters-modal__select"
                    value={favoriteMode}
                    onChange={(e) => setFavoriteMode(e.target.value)}
                    aria-label="Favorite mode"
                  >
                    {FAVORITE_MODE_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>{option.label}</option>
                    ))}
                  </select>
                  <button type="submit" className="filters-modal__btn filters-modal__btn--add">
                    Add
                  </button>
                </div>
              </form>
              {favorites.length === 0 ? (
                <p className="filters-modal__empty">
                  No favorites yet. Type a place or broad term above, choose a mode if you want, and click Add.
                </p>
              ) : (
                <ul className="filters-modal__list">
                  {favorites.map((favorite) => (
                    <li key={`${favorite.mode}-${favorite.placeId || favorite.label}`} className="filters-modal__item">
                      <span className="filters-modal__item-name">{favorite.label}</span>
                      <select
                        className="filters-modal__favorite-mode-select"
                        value={favorite.mode}
                        onChange={(e) => updateFavorite(favorite, { mode: e.target.value })}
                        aria-label={`Favorite mode for ${favorite.label}`}
                      >
                        {FAVORITE_MODE_OPTIONS.map((option) => (
                          <option key={option.value} value={option.value}>{option.label}</option>
                        ))}
                      </select>
                      <button
                        type="button"
                        className="filters-modal__btn filters-modal__btn--remove"
                        onClick={() => removeFavorite(favorite)}
                        aria-label={`Remove ${favorite.label}`}
                      >
                        Remove
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </section>

          <section className="filters-modal__section" aria-labelledby="filters-icons-heading">
            <h3 id="filters-icons-heading" className="filters-modal__section-title">
              Icons
            </h3>
            {showFurnishedFinderControls && (
              <div className="filters-modal__row">
                <label className="filters-modal__checkbox-label">
                  <input
                    type="checkbox"
                    checked={showFFMarkers}
                    onChange={(e) => onShowFFMarkersChange(e.target.checked)}
                    aria-label="Show rental markers on map"
                  />
                  <span>Rentals</span>
                </label>
              </div>
            )}
            <div className="filters-modal__row">
              <label className="filters-modal__checkbox-label">
                <input
                  type="checkbox"
                  checked={showStoreMarkers}
                  onChange={(e) => onShowStoreMarkersChange(e.target.checked)}
                  aria-label="Show grocery markers on map"
                />
                <span>Grocery stores</span>
              </label>
            </div>
            <div className="filters-modal__row">
              <label className="filters-modal__checkbox-label">
                <input
                  type="checkbox"
                  checked={showGymMarkers}
                  onChange={(e) => onShowGymMarkersChange(e.target.checked)}
                  aria-label="Show gym markers on map"
                />
                <span>Gyms</span>
              </label>
            </div>
            <div className="filters-modal__row">
              <label className="filters-modal__checkbox-label">
                <input
                  type="checkbox"
                  checked={showThingsToDo}
                  onChange={(e) => onShowThingsToDoChange(e.target.checked)}
                  aria-label="Show things to do markers on map"
                />
                <span>Things to do</span>
              </label>
            </div>
          </section>

          {/* Section: Filters (map/display) */}
          {showCrimeControls && (
          <section className="filters-modal__section" aria-labelledby="filters-display-heading">
            <h3 id="filters-display-heading" className="filters-modal__section-title">
              Map &amp; display
            </h3>
            <div className="filters-modal__field">
              <label htmlFor="filters-crime-opacity" className="filters-modal__label">
                Crime layer opacity: {Math.round((crimeLayerOpacity ?? 0.45) * 100)}%
              </label>
              <input
                id="filters-crime-opacity"
                type="range"
                min={0}
                max={1}
                step={0.05}
                value={crimeLayerOpacity ?? 0.45}
                onChange={(e) => onCrimeLayerOpacityChange(Number(e.target.value))}
                className="filters-modal__range"
                aria-label="Crime layer transparency"
              />
            </div>
          </section>
          )}
        </div>

        <div className="filters-modal__footer">
          <button type="button" className="filters-modal__btn filters-modal__btn--primary" onClick={onClose}>
            Done
          </button>
        </div>
      </div>
    </div>
  );
}
